import Phaser from 'phaser';
import { attachBottomNav, bottomNavTopY, preloadNavAssets } from '../view/AppNav.js';
import { mountPageHeader } from '../view/PageHeader.js';
import { SectionTabs } from '../view/SectionTabs.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

// Mirrors AboutScene's terminal styling so the two utility screens
// feel like one product. Page background is the same near-black navy
// HomeScene + AboutScene use. The reusable scrollable container +
// header band pattern is duplicated rather than extracted — two
// scenes is below the bar where a shared base would pay off; extract
// only if a third terminal page lands.
const PAGE_BG_INT = 0x040713;

const SIDE_PADDING = 36;
const VIEWPORT_BOTTOM_INSET = 24;

const LINE_GAP_TIGHT = 6;
const LINE_GAP_LOOSE = 18;
const SECTION_GAP = 36;

// Text size scale. At 720 design width, real-device users found the
// body type tight without pinch-and-zoom. Bumping content fonts 1.5×
// is the cheapest fix — tweak this single constant to dial the page
// up or down without editing every text element. Base sizes below
// stay as the design intent; multiplication happens in `fs()`.
const TEXT_SCALE = 1.5;
function fs(base: number): string {
  return `${Math.round(base * TEXT_SCALE)}px`;
}

const BOARD_TARGET_WIDTH = 600;

// Turn-cycle diagram constants. Vertical layout so it scans cleanly
// on a phone — four phase chips stacked, arrows between, looping
// back to the next player. Colors mirror the in-game phase badge
// (see phaseDescriptor in GameScene): cyan / red / green / muted.
const CYCLE_CHIP_WIDTH = 280;
const CYCLE_CHIP_HEIGHT = 52;
const CYCLE_GAP = 22;
const CYCLE_ARROW_LENGTH = CYCLE_GAP - 6;

/**
 * "How to play" briefing. Restructured from the original four-section
 * systems dump into a narrative arc: what the game IS → the board → the
 * turn cycle → walkthrough of a typical turn → combat math → regions →
 * winning → tips. New players were nodding through the old terse
 * bullets but still didn't know what to tap when they opened the game.
 * Prose + a board screenshot + a turn-cycle diagram do the heavy
 * lifting now.
 */
export class HowToScene extends Phaser.Scene {
  private content!: Phaser.GameObjects.Container;
  private contentHeight = 0;
  private contentTop = 0;
  private dragStart: { pointerY: number; contentY: number } | null = null;

  constructor() {
    super('HowTo');
  }

  preload(): void {
    preloadNavAssets(this);
    // Distinct preload key from AboutScene's 'aboutLogo' so the two
    // scenes can coexist without cache collision, even though they
    // currently load the same file.
    this.load.image('howToLogo', '/assets/logo2.png');
    // Ring-map board screenshot. Captured manually from a fresh AI
    // Ring game; lives in /public/assets/. If it's missing the
    // board section falls back to a placeholder text block.
    this.load.image('howToBoard', '/assets/howto-ring.png');
  }

  create(): void {
    this.drawBackground();
    const { contentTop } = mountPageHeader({
      scene: this,
      logoAssetKey: 'howToLogo',
      showBack: true,
    });
    this.contentTop = contentTop;
    this.content = this.add.container(0, contentTop);
    this.buildContent();
    this.applyScrollMask();
    this.installScrollInput();
    attachBottomNav(this, 'settings');
  }

  // --- Background ---

  private drawBackground(): void {
    const g = this.add.graphics();
    g.fillStyle(PAGE_BG_INT, 1);
    g.fillRect(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT);
  }

  // --- Scrollable content ---

  private buildContent(): void {
    let y = 0;

    // Section nav butts directly against the logo (the logo lives in
    // scene root via mountPageHeader; content starts flush below it).
    y = this.addSectionNav(y);
    y += SECTION_GAP;

    y = this.addSectionHeader(y, 'THE GAME');
    y = this.addProse(
      y,
      'BattleShark is pocket Risk. Each player starts holding a portion of the map. Each turn you can reinforce your positions, attack adjacent territories or shuffle reserves through controlled territory. Take every tile in FFA, or be the last team standing, and YOU WIN!'
    );
    y += SECTION_GAP;

    y = this.addSectionHeader(y, 'THE BOARD');
    y = this.addBoardImage(y);
    y += LINE_GAP_LOOSE;
    y = this.addBulletList(
      y,
      [
        'Each square is a TERRITORY. The number on it is how many units defend it.',
        'Color = team. Lines between tiles show which territories are ADJACENT (can attack each other).',
        'Territories grouped together are REGIONS. Hold every tile in a region for a per-turn bonus.',
      ],
      '[+]'
    );
    y += SECTION_GAP;

    y = this.addSectionHeader(y, 'THE TURN CYCLE');
    y = this.addProse(
      y,
      'Every turn moves through the same four phases in order. The active phase shows at the top of the game screen. When it changes color, you know the phase changed.'
    );
    y += LINE_GAP_LOOSE;
    y = this.addTurnCycle(y);
    y += SECTION_GAP;

    y = this.addSectionHeader(y, 'A TYPICAL TURN');
    y = this.addProse(
      y,
      "Here's what each phase actually feels like to play."
    );
    y += LINE_GAP_LOOSE;
    y = this.addNumberedStep(
      y,
      1,
      'DEPLOY',
      "You're given new units to place, and the count shows at the top of the screen. Tap any tile you own to drop one unit on it. Repeat until you've placed them all. Strengthen tiles that border enemies; that's where attacks land.",
      COLORS.accentCyanHex
    );
    y += LINE_GAP_LOOSE;
    y = this.addNumberedStep(
      y,
      2,
      'ATTACK',
      "Tap a tile of yours with 2 or more units (you can't attack from a 1). Then tap an ADJACENT enemy tile. Dice roll: your highest beats their highest, but ties go to the defender. Keep attacking from the same tile until you win, lose, or back off. Conquer the tile (drop them to zero) → choose how many of your attackers march in.",
      COLORS.accentRedHex
    );
    y += LINE_GAP_LOOSE;
    y = this.addNumberedStep(
      y,
      3,
      'FORTIFY',
      "Once per turn, move units between two of your tiles that are connected. Useful for shuffling reserves from a safe interior tile up to the front line. Skip it freely if you don't need it.",
      COLORS.accentGreenHex
    );
    y += LINE_GAP_LOOSE;
    y = this.addNumberedStep(
      y,
      4,
      'END',
      'Hand off to the next player. Cycle starts over for them.',
      COLORS.textSecondary
    );
    y += SECTION_GAP;

    y = this.addSectionHeader(y, 'COMBAT MATH');
    y = this.addProse(
      y,
      'Attacker rolls (units - 1) dice, up to 3 max. Defender rolls (units) dice, up to 2 max. Sort each side high-to-low and pair them off. Defender wins ties.'
    );
    y += LINE_GAP_LOOSE;
    y = this.addBulletList(
      y,
      [
        '4 vs 3 → attacker rolls 3, defender rolls 2',
        '3 vs 2 → attacker rolls 2, defender rolls 2',
        '2 vs 1 → attacker rolls 1, defender rolls 1',
      ],
      '[>]'
    );
    y += SECTION_GAP;

    y = this.addSectionHeader(y, 'REINFORCEMENTS & REGIONS');
    y = this.addBulletList(
      y,
      [
        'Each turn you get max(3, your tile count ÷ 3) new units to deploy.',
        'Hold every tile in a region → bonus units on top of the base reinforcement.',
        'Fully-held regions get a colored RING around them. Visible signal that you’re cooking.',
      ],
      '[+]'
    );
    y += SECTION_GAP;

    y = this.addSectionHeader(y, 'WINNING');
    y = this.addProse(
      y,
      'Take every tile on the map, or be the last team with any tiles left. A player who drops to zero territories is eliminated immediately. In team games, your team wins as soon as the OTHER team is wiped.'
    );
    y += SECTION_GAP;

    y = this.addSectionHeader(y, 'TIPS');
    y = this.addBulletList(
      y,
      [
        "Don't attack 2-vs-2 unless you have to. The dice favor defenders on small rolls.",
        'Holding a full region is worth more than spreading wide. Pick a region, lock it down, expand from there.',
        'You almost never conquer the whole map in one turn. Play for next turn, finishing each turn on a strong border, not a half-finished offensive.',
        'A 1-unit tile is a free target. Anything next to an enemy needs at least 2.',
      ],
      '[!]'
    );
    y += SECTION_GAP;

    y = this.addSignOff(y);

    this.contentHeight = y;
  }

  // Shared 3-tab section nav across Settings / HowTo / About.
  private addSectionNav(y: number): number {
    const tabs = new SectionTabs(this, {
      x: 0,
      y,
      width: DESIGN_WIDTH,
      items: [
        { id: 'howto', label: 'HOW TO PLAY' },
        { id: 'settings', label: 'SETTINGS' },
        { id: 'about', label: 'INFO' },
      ],
      activeIndex: 0,
      onSelect: (id) => {
        // Intra-group navigation — see SettingsScene.addSectionNav
        // for the rationale. Plain scene.start() preserves the
        // history pointer at whatever opened the group.
        if (id === 'settings') this.scene.start('Settings');
        else if (id === 'about') this.scene.start('About');
      },
    });
    this.content.add(tabs.root);
    return y + tabs.height;
  }

  /**
   * Ring-map screenshot or, if the asset hasn't been dropped in yet,
   * a placeholder text block. Lives in /public/assets/howto-ring.png
   * — capture from a fresh AI Ring game so the colors + dice counts
   * match what a new player will see on day one.
   */
  private addBoardImage(y: number): number {
    if (this.textures.exists('howToBoard')) {
      const img = this.add.image(DESIGN_WIDTH / 2, y, 'howToBoard');
      const scale = Math.min(1, BOARD_TARGET_WIDTH / img.width);
      img.setScale(scale);
      img.setOrigin(0.5, 0);
      this.content.add(img);
      return y + img.height * scale;
    }
    // Asset missing — render a placeholder so the page still
    // builds. Drop howto-ring.png in /public/assets to populate.
    const placeholder = this.add
      .text(
        DESIGN_WIDTH / 2,
        y,
        '[ board screenshot missing\n  drop howto-ring.png in /public/assets ]',
        {
          fontFamily: FONT_MONO,
          fontSize: fs(12),
          color: COLORS.textMuted,
          align: 'center',
        }
      )
      .setOrigin(0.5, 0);
    this.content.add(placeholder);
    return y + placeholder.height;
  }

  /**
   * Vertical turn-cycle diagram. Four colored chips stacked with
   * arrows between, looping back at the bottom. Colors match the
   * in-game phase badge so a player who's seen this diagram
   * recognizes "oh that's the deploy color" when they boot a game.
   */
  private addTurnCycle(y: number): number {
    const cx = DESIGN_WIDTH / 2;
    const chips: ReadonlyArray<{ label: string; int: number; hex: string }> = [
      {
        label: '1. DEPLOY',
        int: COLORS.accentCyanInt,
        hex: COLORS.accentCyanHex,
      },
      {
        label: '2. ATTACK',
        int: COLORS.accentRedInt,
        hex: COLORS.accentRedHex,
      },
      {
        label: '3. FORTIFY',
        int: COLORS.accentGreenInt,
        hex: COLORS.accentGreenHex,
      },
      {
        label: '4. END TURN',
        int: COLORS.borderBrightInt,
        hex: COLORS.textSecondary,
      },
    ];

    let cursorY = y;
    chips.forEach((chip, idx) => {
      // Chip body
      const bg = this.add
        .rectangle(
          cx,
          cursorY + CYCLE_CHIP_HEIGHT / 2,
          CYCLE_CHIP_WIDTH,
          CYCLE_CHIP_HEIGHT,
          COLORS.bgDeepInt
        )
        .setStrokeStyle(2, chip.int, 0.9);
      this.content.add(bg);

      const label = this.add
        .text(cx, cursorY + CYCLE_CHIP_HEIGHT / 2, chip.label, {
          fontFamily: FONT_HEADER,
          fontSize: fs(14),
          color: chip.hex,
          fontStyle: '700',
        })
        .setLetterSpacing(2)
        .setOrigin(0.5);
      this.content.add(label);

      cursorY += CYCLE_CHIP_HEIGHT;

      // Arrow between chips (skip after the last one — we draw the
      // loop-back hint instead).
      if (idx < chips.length - 1) {
        const arrow = this.add
          .text(cx, cursorY + CYCLE_GAP / 2, '↓', {
            fontFamily: FONT_MONO,
            fontSize: fs(16),
            color: COLORS.textSecondary,
          })
          .setOrigin(0.5);
        this.content.add(arrow);
        cursorY += CYCLE_GAP;
      } else {
        // Loop-back annotation under the last chip
        const loopHint = this.add
          .text(cx, cursorY + 8, '↻  next player', {
            fontFamily: FONT_MONO,
            fontSize: fs(12),
            color: COLORS.textMuted,
          })
          .setOrigin(0.5, 0);
        this.content.add(loopHint);
        cursorY += loopHint.height + 12;
      }
    });

    // Suppress unused-variable warning for CYCLE_ARROW_LENGTH —
    // reserved for if we ever swap the unicode arrow for a drawn
    // line (better cross-font consistency).
    void CYCLE_ARROW_LENGTH;

    return cursorY;
  }

  /**
   * Numbered walkthrough step. Title in the phase color, body as a
   * wrapped paragraph below. Used in A TYPICAL TURN to step a new
   * player through deploy → attack → fortify → end.
   */
  private addNumberedStep(
    y: number,
    _num: number,
    title: string,
    body: string,
    titleHex: string
  ): number {
    void _num; // baked into the title string for now; reserved for
    // future swap to a numbered badge graphic.
    const titleEl = this.add
      .text(SIDE_PADDING, y, title, {
        fontFamily: FONT_HEADER,
        fontSize: fs(15),
        color: titleHex,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0, 0);
    this.content.add(titleEl);
    y += titleEl.height + 6;

    const bodyEl = this.add
      .text(SIDE_PADDING, y, body, {
        fontFamily: FONT_MONO,
        fontSize: fs(14),
        color: COLORS.textPrimary,
        wordWrap: { width: DESIGN_WIDTH - SIDE_PADDING * 2 },
      })
      .setLineSpacing(3)
      .setOrigin(0, 0);
    this.content.add(bodyEl);
    return y + bodyEl.height;
  }

  /**
   * Single wrapped paragraph, no bullet marker. For lede / intro
   * copy where a [+] would feel listy.
   */
  private addProse(y: number, text: string): number {
    const el = this.add
      .text(SIDE_PADDING, y, text, {
        fontFamily: FONT_MONO,
        fontSize: fs(14),
        color: COLORS.textPrimary,
        wordWrap: { width: DESIGN_WIDTH - SIDE_PADDING * 2 },
      })
      .setLineSpacing(3)
      .setOrigin(0, 0);
    this.content.add(el);
    return y + el.height;
  }

  private addSignOff(y: number): number {
    const cx = DESIGN_WIDTH / 2;

    const rule = this.add
      .text(cx, y, '─'.repeat(38), {
        fontFamily: FONT_MONO,
        fontSize: fs(14),
        color: COLORS.textMuted,
      })
      .setOrigin(0.5, 0);
    this.content.add(rule);
    y += rule.height + LINE_GAP_TIGHT;

    const signoff = this.add
      .text(cx, y, '// END OF BRIEFING', {
        fontFamily: FONT_MONO,
        fontSize: fs(13),
        color: COLORS.textMuted,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5, 0);
    this.content.add(signoff);
    return y + signoff.height;
  }

  // --- Section helpers ---

  private addSectionHeader(y: number, title: string): number {
    const header = this.add
      .text(SIDE_PADDING, y, `> ${title}`, {
        fontFamily: FONT_MONO,
        fontSize: fs(17),
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(1)
      .setOrigin(0, 0);
    this.content.add(header);
    y += header.height + 4;

    const rule = this.add
      .text(SIDE_PADDING, y, '─'.repeat(34), {
        fontFamily: FONT_MONO,
        fontSize: fs(13),
        color: intToHex(COLORS.borderSubtleInt),
      })
      .setOrigin(0, 0);
    this.content.add(rule);
    return y + rule.height + LINE_GAP_LOOSE;
  }

  private addBulletList(
    y: number,
    items: readonly string[],
    marker: string,
    indent: number = SIDE_PADDING
  ): number {
    const wrapWidth = DESIGN_WIDTH - SIDE_PADDING - indent;
    for (const item of items) {
      const line = this.add
        .text(indent, y, `${marker} ${item}`, {
          fontFamily: FONT_MONO,
          fontSize: fs(14),
          color: COLORS.textPrimary,
          wordWrap: { width: wrapWidth },
        })
        .setLineSpacing(2)
        .setOrigin(0, 0);
      this.content.add(line);
      y += line.height + LINE_GAP_TIGHT;
    }
    return y;
  }

  // --- Scroll input ---

  private applyScrollMask(): void {
    // Scroll-visible area now stops above the bottom NavBar so
    // content doesn't bleed under it.
    const maskBottom = bottomNavTopY();
    const shape = this.make.graphics({});
    shape.fillStyle(0xffffff, 1);
    shape.fillRect(
      0,
      this.contentTop,
      DESIGN_WIDTH,
      maskBottom - this.contentTop
    );
    const mask = shape.createGeometryMask();
    this.content.setMask(mask);
  }

  private installScrollInput(): void {
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      // Don't initiate drag from inside the page header (logo +
      // Back overlay) or the bottom nav strip.
      if (p.y < this.contentTop) return;
      if (p.y > bottomNavTopY()) return;
      this.dragStart = { pointerY: p.y, contentY: this.content.y };
    });
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!p.isDown || !this.dragStart) return;
      const dy = p.y - this.dragStart.pointerY;
      this.content.y = this.clampScroll(this.dragStart.contentY + dy);
    });
    this.input.on('pointerup', () => {
      this.dragStart = null;
    });
    this.input.on('pointerupoutside', () => {
      this.dragStart = null;
    });

    this.input.on(
      'wheel',
      (
        _p: Phaser.Input.Pointer,
        _go: Phaser.GameObjects.GameObject[],
        _dx: number,
        deltaY: number
      ) => {
        this.content.y = this.clampScroll(this.content.y - deltaY);
      }
    );
  }

  private clampScroll(rawY: number): number {
    // Viewport now ends at the AppNav strip, not the canvas bottom.
    const viewportBottom = bottomNavTopY() - VIEWPORT_BOTTOM_INSET;
    const minY = Math.min(this.contentTop, viewportBottom - this.contentHeight);
    const maxY = this.contentTop;
    if (rawY > maxY) return maxY;
    if (rawY < minY) return minY;
    return rawY;
  }
}

function intToHex(int: number): string {
  return `#${int.toString(16).padStart(6, '0')}`;
}
